/**
 * Event dispatcher — dispatches canonical events to all granted pixel SDKs.
 */

import type { ConsentMap, ModuleConfig, PixelConfig } from '../types/consent.d';

// Canonical event → per-platform event names
const EVENT_MAP: Record<string, Record<string, string>> = {
  page_view: {
    ga4: 'page_view', google_ads: 'page_view',
    meta: 'PageView', tiktok: 'PageView',
    pinterest: 'pagevisit', snapchat: 'PAGE_VIEW', x_twitter: 'tw-pageview',
  },
  purchase: {
    ga4: 'purchase', google_ads: 'conversion',
    meta: 'Purchase', tiktok: 'Purchase',
    pinterest: 'checkout', snapchat: 'PURCHASE', x_twitter: 'tw-purchase',
  },
  add_to_cart: {
    ga4: 'add_to_cart', google_ads: 'add_to_cart',
    meta: 'AddToCart', tiktok: 'AddToCart',
    pinterest: 'addtocart', snapchat: 'ADD_CART', x_twitter: '',
  },
  view_item: {
    ga4: 'view_item', google_ads: 'view_item',
    meta: 'ViewContent', tiktok: 'ViewContent',
    pinterest: 'pagevisit', snapchat: 'VIEW_CONTENT', x_twitter: '',
  },
  begin_checkout: {
    ga4: 'begin_checkout', google_ads: 'begin_checkout',
    meta: 'InitiateCheckout', tiktok: 'InitiateCheckout',
    pinterest: 'checkout', snapchat: 'START_CHECKOUT', x_twitter: '',
  },
  search: {
    ga4: 'search', google_ads: 'search',
    meta: 'Search', tiktok: 'Search',
    pinterest: 'search', snapchat: 'SEARCH', x_twitter: '',
  },
  complete_registration: {
    ga4: 'sign_up', google_ads: '',
    meta: 'CompleteRegistration', tiktok: '',
    pinterest: '', snapchat: 'SIGN_UP', x_twitter: '',
  },
};

function findCanonicalEvent(platformEvent: string): string {
  for (const canonical in EVENT_MAP) {
    const map = EVENT_MAP[canonical];
    for (const type in map) {
      if (map[type] === platformEvent) return canonical;
    }
  }
  return platformEvent.toLowerCase();
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function fireEvent(
  canonicalEvent: string,
  data: Record<string, unknown>,
  config: ModuleConfig,
  consent: ConsentMap,
  injected: Record<string, boolean>,
): void {
  const mapping = EVENT_MAP[canonicalEvent];
  if (!mapping) return;

  for (const pixel of config.pixels) {
    if (!isGranted(pixel.category, consent)) continue;

    const key = pixel.type + '_' + (pixel.pixel_id || 'custom');
    if (!injected[key]) continue;

    const platformEvent = mapping[pixel.type] || '';
    if (!platformEvent) continue;

    firePixelEvent(pixel, platformEvent, data);
  }
}

function isGranted(category: string, consent: ConsentMap): boolean {
  if (category === 'necessary') return true;
  return !!consent[category as keyof ConsentMap];
}

function firePixelEvent(pixel: PixelConfig, eventName: string, data: Record<string, unknown>): void {
  switch (pixel.type) {
    case 'ga4':
      if (typeof gtag === 'function') {
        gtag('event', eventName, data);
      }
      break;

    case 'google_ads':
      if (typeof gtag === 'function') {
        const adsData: Record<string, unknown> = { ...data };
        if (pixel.extra_config?.conversion_label) {
          adsData.send_to = pixel.pixel_id + '/' + pixel.extra_config.conversion_label;
        }
        gtag('event', eventName, adsData);
      }
      break;

    case 'meta':
      if (typeof fbq === 'function') {
        const eventIdCookie = 'flt_tm_event_id_' + findCanonicalEvent(eventName);
        const eventId = getCookie(eventIdCookie);
        const opts: Record<string, unknown> = {};
        if (eventId) {
          opts.eventID = eventId;
          document.cookie = eventIdCookie + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }
        fbq('track', eventName, data, opts);
      }
      break;

    case 'tiktok':
      if (typeof ttq !== 'undefined' && ttq?.track) {
        ttq.track(eventName, data as Record<string, unknown>);
      }
      break;

    case 'pinterest':
      if (typeof pintrk === 'function') {
        pintrk('track', eventName, data);
      }
      break;

    case 'snapchat':
      if (typeof snaptr === 'function') {
        snaptr('track', eventName, data);
      }
      break;

    case 'x_twitter':
      if (typeof twq === 'function') {
        twq('track', eventName, data);
      }
      break;
  }
}
